// Feedback a traveler chose to share with their host (see the host_trip_feedback()
// function): only host-visible survey answers, only from travelers who said yes.

export interface FeedbackAnswer {
  label: string;
  kind: 'stars' | 'scale10' | 'choice' | 'multi' | 'yesno' | 'text';
  role: string | null;
  value: unknown;
  note: string | null;
}

export interface FeedbackItem {
  survey_id: string;
  completed_at: string;
  traveler_first_name: string | null;
  trip_name: string | null;
  answers: FeedbackAnswer[];
}

/** "{host}" in a question becomes the host's own first name; "{trip}" the trip title. */
export function fillForHost(label: string, hostFirstName: string | null | undefined, trip?: string | null): string {
  return label
    .replace(/\{host\}/g, hostFirstName || 'you')
    .replace(/\{trip\}/g, trip ? `“${trip}”` : 'the trip');
}

/** The traveler's star rating of the host, when they gave one. */
export function hostRatingOf(item: FeedbackItem): number | null {
  const a = item.answers.find((x) => x.role === 'host_rating');
  return typeof a?.value === 'number' ? a.value : null;
}

/** Average of the host ratings across all shared feedback, rounded to one decimal. */
export function averageHostRating(items: FeedbackItem[]): { average: number | null; count: number } {
  const ratings = items.map(hostRatingOf).filter((v): v is number => v !== null);
  if (ratings.length === 0) return { average: null, count: 0 };
  const avg = ratings.reduce((s, n) => s + n, 0) / ratings.length;
  return { average: Math.round(avg * 10) / 10, count: ratings.length };
}

/** Plain-text form of a non-star answer, or null when there is nothing to show. */
export function answerText(a: FeedbackAnswer): string | null {
  const v = a.value;
  if (a.kind === 'text') return typeof v === 'string' && v.trim() ? v.trim() : null;
  if (a.kind === 'yesno') return v === true ? 'Yes' : v === false ? 'No' : null;
  if (a.kind === 'multi') return Array.isArray(v) && v.length ? v.join(', ') : null;
  if (typeof v === 'string' || typeof v === 'number') return String(v);
  return null;
}
